import React, { useState, useMemo, useRef, useEffect } from 'react';
import '../styles/DateSelector.css';

const DateSelector = ({ availableDates, selectedIndex, onIndexChange, dateAnimation }) => {
  // アニメーション状態
  const [slideDirection, setSlideDirection] = useState('');
  const [buttonAnimation, setButtonAnimation] = useState('');
  
  // アニメーション管理用の参照
  const animationTimeout = useRef(null);
  
  // 現在選択されている日付
  const currentDate = useMemo(() => {
    return availableDates[selectedIndex] || null;
  }, [availableDates, selectedIndex]);
  
  // 日付のフォーマット
  const formattedDateDisplay = useMemo(() => {
    if (!currentDate) return '';
    
    // 日付の場合はフォーマット
    const date = new Date(currentDate);
    
    // 有効な日付かチェック
    if (isNaN(date.getTime())) {
      console.error('無効な日付:', currentDate);
      return '日付エラー';
    }
    
    const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    const dayOfWeek = days[date.getDay()];
    
    return (
      <>
        {formattedDate} <span className="day-label">{dayOfWeek}</span>
      </>
    );
  }, [currentDate]);
  
  // 範囲チェック
  const canGoPrev = selectedIndex > 0;
  const canGoNext = selectedIndex < availableDates.length - 1;
  
  // 前の日付に移動
  const goToPrevDay = () => {
    if (!canGoPrev) return;
    
    // 既存のアニメーションタイマーをクリア
    if (animationTimeout.current) {
      clearTimeout(animationTimeout.current);
      setButtonAnimation('');
    }
    
    // ボタンアニメーション開始
    setButtonAnimation('gradient-sweep-right');
    
    const newIndex = selectedIndex - 1;
    
    // アニメーション開始と同時にコールバック実行
    if (onIndexChange) onIndexChange(newIndex);
    
    // アニメーション終了後にクラスをリセット
    animationTimeout.current = setTimeout(() => {
      setButtonAnimation('');
      animationTimeout.current = null;
    }, 200);
  };
  
  // 次の日付に移動
  const goToNextDay = () => {
    if (!canGoNext) return;
    
    // 既存のアニメーションタイマーをクリア
    if (animationTimeout.current) {
      clearTimeout(animationTimeout.current);
      setButtonAnimation('');
    }
    
    // ボタンアニメーション開始
    setButtonAnimation('gradient-sweep-left');
    
    const newIndex = selectedIndex + 1;
    
    // アニメーション開始と同時にコールバック実行
    if (onIndexChange) onIndexChange(newIndex);
    
    // アニメーション終了後にクラスをリセット
    animationTimeout.current = setTimeout(() => {
      setButtonAnimation('');
      animationTimeout.current = null;
    }, 200);
  };
  
  // クリーンアップ処理
  useEffect(() => {
    return () => {
      if (animationTimeout.current) {
        clearTimeout(animationTimeout.current);
      }
    };
  }, []);
  
  // 選択インデックスが変更されたときにslideDirectionをリセット
  useEffect(() => {
    setSlideDirection('');
  }, [selectedIndex]);
  
  // 利用可能な日付がない場合は何も表示しない
  if (availableDates.length === 0) {
    return null;
  }
  
  return (
    <div className="date-picker-1">
      <button 
        className={`arrow-btn-1 prev ${!canGoPrev ? 'disabled' : ''} ${buttonAnimation === 'gradient-sweep-right' ? 'gradient-sweep-right' : ''}`} 
        onClick={goToPrevDay}
        disabled={!canGoPrev}
      >
        <span className="arrow">&#10094;</span>
      </button>
      <div className={`date-display-1 ${slideDirection} ${dateAnimation || ''}`}>
        {formattedDateDisplay}
      </div>
      <button 
        className={`arrow-btn-1 next ${!canGoNext ? 'disabled' : ''} ${buttonAnimation === 'gradient-sweep-left' ? 'gradient-sweep-left' : ''}`} 
        onClick={goToNextDay}
        disabled={!canGoNext}
      >
        <span className="arrow">&#10095;</span>
      </button>
    </div>
  );
};

export default DateSelector;
